import { CentralizedUrlAdapter } from '../adapters/CentralizedUrlAdapter';
import {
  BillingInfoFormController,
  EmailPlansController,
  OrderSummaryController,
  PayflowProgressBarController,
  PaymentButtonController,
  PaymentMethodsController,
  PricingConfiguratorController,
  PayflowTrackActionsController,
  PrepayController,
  PromoCodesController,
  ThankYouController,
  ValueFocusedMWebTrackingController,
} from '../controllers';
import { OrderSummarySetupController } from '../controllers/OrderSummarySetupController';
import { RouterNonUiController } from '../controllers/RouterNonUiController';
import { initializerHelpers } from '../helpers';
import type { Listeners } from '../helpers';
import {
  AlertManager,
  OrderSummaryManager,
  PayflowProgressBarManager,
  PaymentButtonManager,
  PricingConfiguratorManager,
  PromoCodesManager,
  EmailPlansManager,
  AddOnsManager,
  PaymentMethodsManager,
  BillingInfoFormManager,
  ThankYouManager,
  RouterNonUiManager,
  PlanComparisonTableManager,
  PrepaySelectManager,
  ContactsTierSelectManager,
  CurrencyManager,
  NonProfitManager,
  AddOnsSubscriptionManager,
  UserPermissionBannerManager,
  PaymentProcessingInfoManager,
  PayflowTopBarManager,
  ScheduledDowngradeBannerManager,
  StripeOverdueBannerManager,
  InboundPromoCodesManager,
} from '../managers';

export interface PayflowInitializerConfig {
  /**
   * Whether to instantiate the InboundPromoCodesManager.
   * Set to true for apps that need to capture promo codes from URL params and partner IDs.
   * Set to false (or omit) for apps that don't need this functionality.
   * @default false
   */
  inboundPromoCodes?: boolean;
}

export class PayflowInitializer {
  public listeners: Listeners;

  // managers
  private orderSummaryManager: OrderSummaryManager;

  private progressBarManager: PayflowProgressBarManager;

  private alertManager: AlertManager;

  private paymentButtonManager: PaymentButtonManager;

  private pricingConfiguratorManager: PricingConfiguratorManager;

  private prepaySelectManager: PrepaySelectManager;

  private contactsTierSelectManager: ContactsTierSelectManager;

  private currencyManager: CurrencyManager;

  private nonProfitManager: NonProfitManager;

  private thankYouManager: ThankYouManager;

  private promoCodesManager: PromoCodesManager;

  private inboundPromoCodesManager: InboundPromoCodesManager | undefined;

  private emailPlansManager: EmailPlansManager;

  private paymentMethodsManager: PaymentMethodsManager;

  private billingInfoFormManager: BillingInfoFormManager;

  private addOnsManager: AddOnsManager;

  private planComparisonTableManager: PlanComparisonTableManager;

  private routerManager: RouterNonUiManager;

  private addOnsSubscriptionManager: AddOnsSubscriptionManager;

  private userPermissionBannerManager: UserPermissionBannerManager;

  private paymentProcessingInfoManager: PaymentProcessingInfoManager;

  private payflowTopBarManager: PayflowTopBarManager;

  private scheduledDowngradeBannerManager: ScheduledDowngradeBannerManager;

  private stripeOverdueBannerManager: StripeOverdueBannerManager;

  // controllers
  public thankYouController: ThankYouController;

  // init methods
  public orderSummaryInit: OrderSummaryManager['orderSummaryInit'];

  public payflowProgressBarInit: PayflowProgressBarManager['payflowProgressBarInit'];

  public alertInit: AlertManager['alertInit'];

  public paymentButtonInit: PaymentButtonManager['paymentButtonInit'];

  public pricingConfiguratorInit: PricingConfiguratorManager['pricingConfiguratorInit'];

  public prepaySelectInit: PrepaySelectManager['prepaySelectInit'];

  public contactsTierSelectInit: ContactsTierSelectManager['contactsTierSelectInit'];

  public currencyInit: CurrencyManager['currencyInit'];

  public nonProfitInit: NonProfitManager['nonProfitInit'];

  public thankYouInit: ThankYouManager['thankYouInit'];

  public promoCodesInit: PromoCodesManager['promoCodesInit'];

  public inboundPromoCodesInit: InboundPromoCodesManager['inboundPromoCodesInit'] | undefined;

  public emailPlansInit: EmailPlansManager['emailPlansInit'];

  public paymentMethodsInit: PaymentMethodsManager['paymentMethodsInit'];

  public billingInfoFormInit: BillingInfoFormManager['billingInfoFormInit'];

  public addOnsInit: AddOnsManager['addOnsInit'];

  public addOnsSubscriptionInit: AddOnsSubscriptionManager['addOnsSubscriptionInit'];

  public planComparisonTableInit: PlanComparisonTableManager['planComparisonTableInit'];

  public routerNonUiInit: RouterNonUiManager['routerNonUiInit'];

  public userPermissionBannerInit: UserPermissionBannerManager['userPermissionBannerInit'];

  public paymentProcessingInfoInit: PaymentProcessingInfoManager['paymentProcessingInfoInit'];

  public payflowTopBarInit: PayflowTopBarManager['payflowTopBarInit'];

  public scheduledDowngradeBannerInit: ScheduledDowngradeBannerManager['scheduledDowngradeBannerInit'];

  public stripeOverdueBannerInit: StripeOverdueBannerManager['stripeOverdueBannerInit'];

  constructor(config: PayflowInitializerConfig = {}) {
    const urlAdapter = new CentralizedUrlAdapter();

    this.orderSummaryManager = new OrderSummaryManager();
    this.progressBarManager = new PayflowProgressBarManager();
    this.alertManager = new AlertManager();
    this.thankYouController = new ThankYouController(this.alertManager);
    this.paymentButtonManager = new PaymentButtonManager();
    this.pricingConfiguratorManager = new PricingConfiguratorManager();
    this.prepaySelectManager = new PrepaySelectManager(urlAdapter);
    this.contactsTierSelectManager = new ContactsTierSelectManager(urlAdapter);
    this.currencyManager = new CurrencyManager(urlAdapter);
    this.nonProfitManager = new NonProfitManager(urlAdapter);
    this.thankYouManager = new ThankYouManager();
    this.promoCodesManager = new PromoCodesManager(urlAdapter);
    this.emailPlansManager = new EmailPlansManager(urlAdapter);
    this.paymentMethodsManager = new PaymentMethodsManager(urlAdapter);
    this.billingInfoFormManager = new BillingInfoFormManager();
    this.addOnsManager = new AddOnsManager(urlAdapter);
    this.planComparisonTableManager = new PlanComparisonTableManager();
    this.routerManager = new RouterNonUiManager();
    this.addOnsSubscriptionManager = new AddOnsSubscriptionManager();
    this.userPermissionBannerManager = new UserPermissionBannerManager();
    this.paymentProcessingInfoManager = new PaymentProcessingInfoManager();
    this.payflowTopBarManager = new PayflowTopBarManager(urlAdapter);
    this.scheduledDowngradeBannerManager = new ScheduledDowngradeBannerManager();
    this.stripeOverdueBannerManager = new StripeOverdueBannerManager();

    // Conditionally instantiate InboundPromoCodesManager
    if (config.inboundPromoCodes) {
      this.inboundPromoCodesManager = new InboundPromoCodesManager(urlAdapter);
    }

    this.orderSummaryInit = this.orderSummaryManager.orderSummaryInit;
    this.payflowProgressBarInit = this.progressBarManager.payflowProgressBarInit;
    this.alertInit = this.alertManager.alertInit;
    this.paymentButtonInit = this.paymentButtonManager.paymentButtonInit;
    this.pricingConfiguratorInit = this.pricingConfiguratorManager.pricingConfiguratorInit;
    this.prepaySelectInit = this.prepaySelectManager.prepaySelectInit;
    this.contactsTierSelectInit = this.contactsTierSelectManager.contactsTierSelectInit;
    this.currencyInit = this.currencyManager.currencyInit;
    this.nonProfitInit = this.nonProfitManager.nonProfitInit;
    this.thankYouInit = this.thankYouManager.thankYouInit;
    this.promoCodesInit = this.promoCodesManager.promoCodesInit;
    this.inboundPromoCodesInit = this.inboundPromoCodesManager?.inboundPromoCodesInit;
    this.emailPlansInit = this.emailPlansManager.emailPlansInit;
    this.paymentMethodsInit = this.paymentMethodsManager.paymentMethodsInit;
    this.billingInfoFormInit = this.billingInfoFormManager.billingInfoFormInit;
    this.addOnsInit = this.addOnsManager.addOnsInit;
    this.addOnsSubscriptionInit = this.addOnsSubscriptionManager.addOnsSubscriptionInit;
    this.planComparisonTableInit = this.planComparisonTableManager.planComparisonTableInit;
    this.routerNonUiInit = this.routerManager.routerNonUiInit;
    this.userPermissionBannerInit = this.userPermissionBannerManager.userPermissionBannerInit;
    this.paymentProcessingInfoInit = this.paymentProcessingInfoManager.paymentProcessingInfoInit;
    this.payflowTopBarInit = this.payflowTopBarManager.payflowTopBarInit;
    this.scheduledDowngradeBannerInit = this.scheduledDowngradeBannerManager.scheduledDowngradeBannerInit;
    this.stripeOverdueBannerInit = this.stripeOverdueBannerManager.stripeOverdueBannerInit;

    this.listeners = this.initializeControllers();
  }

  private initializeControllers(): Listeners {
    const controllers = [
      new PromoCodesController(this.orderSummaryManager, this.promoCodesManager, this.inboundPromoCodesManager),
      new PricingConfiguratorController(this.paymentButtonManager),
      new EmailPlansController(this.routerManager, this.progressBarManager, this.addOnsManager),
      new PaymentButtonController(
        this.paymentMethodsManager,
        this.billingInfoFormManager,
        this.routerManager,
        this.paymentButtonManager,
        this.nonProfitManager,
        this.currencyManager,
        this.prepaySelectManager,
        this.contactsTierSelectManager,
        this.orderSummaryManager,
        this.alertManager,
        this.emailPlansManager,
        this.paymentProcessingInfoManager,
        this.progressBarManager,
        this.addOnsSubscriptionManager,
        this.addOnsManager
      ),
      new BillingInfoFormController(
        this.billingInfoFormManager,
        this.paymentMethodsManager,
        this.orderSummaryManager,
        this.paymentButtonManager
      ),
      new OrderSummaryController(
        this.paymentButtonManager,
        this.routerManager,
        this.addOnsManager,
        this.progressBarManager,
        this.prepaySelectManager
      ),
      new PaymentMethodsController(
        this.paymentMethodsManager,
        this.alertManager,
        undefined,
        this.orderSummaryManager,
        this.paymentButtonManager
      ),
      new OrderSummarySetupController(
        this.orderSummaryManager,
        this.billingInfoFormManager,
        this.nonProfitManager,
        this.currencyManager,
        this.prepaySelectManager,
        this.contactsTierSelectManager,
        this.emailPlansManager,
        this.promoCodesManager,
        this.addOnsManager
      ),
      new PayflowProgressBarController(
        this.routerManager,
        this.progressBarManager,
        this.emailPlansManager,
        this.prepaySelectManager,
        this.contactsTierSelectManager,
        this.promoCodesManager,
        this.orderSummaryManager,
        this.inboundPromoCodesManager,
        this.addOnsManager,
        this.nonProfitManager,
        this.payflowTopBarManager
      ),
      new RouterNonUiController(this.progressBarManager),
      new PayflowTrackActionsController(
        this.emailPlansManager,
        this.contactsTierSelectManager,
        this.nonProfitManager,
        this.prepaySelectManager
      ),
      new PrepayController(this.prepaySelectManager),

      new ValueFocusedMWebTrackingController(),
    ];

    initializerHelpers.initControllers(controllers);

    return initializerHelpers.createListeners(controllers);
  }

  public handleAddOnsContinueClick = () => {
    this.progressBarManager.getApiWhenReady().then((api) => api.goToNextStep());
  };

  public handlePaymentPlansContinueClick = () => {
    this.progressBarManager.getApiWhenReady().then((api) => api.goToNextStep());
  };
}
